import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useCoachContext } from "@/contexts/CoachContext";

interface Service {
  icon: string;
  title: string;
  subtitle: string;
  description: string;
  highlights: string[];
  color: "primary" | "gold" | "accent";
}

interface PackageItem {
  name: string;
  duration: string;
  features: string[];
  featured: boolean;
}

interface PackageCategory {
  category: string;
  items: PackageItem[];
}

interface AddOn {
  name: string;
  price: string;
}

interface Testimonial {
  name: string;
  role: string;
  quote: string;
  transformation: string;
  rating: number;
}

export interface WebsiteContent {
  // Hero
  hero_badge_text: string;
  hero_headline: string;
  hero_subheadline: string;
  hero_cta_primary: string;
  hero_cta_secondary: string;
  hero_stat_1_value: string;
  hero_stat_1_label: string;
  hero_stat_2_value: string;
  hero_stat_2_label: string;
  hero_stat_3_value: string;
  hero_stat_3_label: string;
  // About
  about_name: string;
  about_title: string;
  about_intro: string;
  about_story: string;
  about_philosophy_title: string;
  about_philosophy_text: string;
  about_quote: string;
  // Services
  services_badge: string;
  services_title: string;
  services_subtitle: string;
  services: Service[];
  // Packages
  packages_badge: string;
  packages_title: string;
  packages_subtitle: string;
  packages: PackageCategory[];
  packages_addons: AddOn[];
  // Testimonials
  testimonials_badge: string;
  testimonials_title: string;
  testimonials_subtitle: string;
  testimonials: Testimonial[];
  testimonials_cta_text: string;
  // Contact
  contact_badge: string;
  contact_title: string;
  contact_subtitle: string;
  contact_cta_title: string;
  contact_cta_description: string;
  contact_cta_button: string;
  contact_quote: string;
  contact_quote_author: string;
  // Section visibility
  show_about: boolean;
  show_services: boolean;
  show_packages: boolean;
  show_testimonials: boolean;
  show_videos: boolean;
  show_contact: boolean;
}

const defaultServices: Service[] = [
  {
    icon: "Crown",
    title: "Leadership Transformation",
    subtitle: "For Executives & Emerging Leaders",
    description: "Transforming lives of executives to find their voice, own the room, and climb the career ladder. Navigate challenges with confidence, authenticity, and emotional intelligence.",
    highlights: ["Executive presence & communication", "Strategic vision development", "Stress & burnout management", "Team leadership mastery"],
    color: "primary"
  },
  {
    icon: "GraduationCap",
    title: "Career Clarity",
    subtitle: "For Students & Young Professionals",
    description: "Guiding the next generation of leaders to navigate their next steps intentionally. Discover strengths, choose the right paths, and design meaningful careers aligned with passion.",
    highlights: ["Strengths & personality assessment", "Major & career path selection", "Resume & LinkedIn optimization", "Interview confidence building"],
    color: "gold"
  },
  {
    icon: "Heart",
    title: "Women's Empowerment",
    subtitle: "For Women Ready to Rise",
    description: "Supporting women to find their voice, build confidence, and balance ambition with wellness. Navigate life's transitions and challenges with intention, purpose, and joy.",
    highlights: ["Confidence & self-worth building", "Boundary setting mastery", "Identity rebuilding", "Emotional resilience tools"],
    color: "accent"
  }
];

const defaultPackages: PackageCategory[] = [
  {
    category: "Leadership Coaching",
    items: [
      { name: "Starter Leadership", duration: "3 weeks • 6 sessions", features: ["6 x 60-min sessions", "Leadership strengths assessment", "Personalized growth plan", "Weekly check-ins"], featured: false },
      { name: "Executive Transformation", duration: "12-24 weeks", features: ["12 x 60-min sessions", "Full leadership persona assessment", "Vision & strategic clarity roadmap", "Advanced communication training", "Unlimited messaging support"], featured: true }
    ]
  },
  {
    category: "Career Clarity",
    items: [
      { name: "Career Discovery", duration: "2 sessions", features: ["Interests & strengths evaluation", "Career path identification", "Study-life balance coaching"], featured: false },
      { name: "Future-Ready Premium", duration: "8 sessions", features: ["Comprehensive life purpose analysis", "College → career transition roadmap", "Habit building & discipline coaching", "Weekly email accountability"], featured: true }
    ]
  },
  {
    category: "Women's Empowerment",
    items: [
      { name: "EmpowerHer Starter", duration: "2 sessions", features: ["Clarifying current challenges", "Confidence-building tools", "Boundary-setting fundamentals"], featured: false },
      { name: "Rise Again Premium", duration: "12 sessions", features: ["Full mindset transformation", "Inner child & belief reprogramming", "Lifestyle & purpose alignment", "Personalized workbook", "Graduation empowerment ceremony"], featured: true }
    ]
  }
];

const defaultAddons: AddOn[] = [
  { name: "Single 60-min session", price: "$300" },
  { name: "Emergency clarity call (30 min)", price: "$150" },
  { name: "Resume, LinkedIn & Career Review", price: "$150" },
  { name: "Energy Leadership Index", price: "$150" }
];

const defaultTestimonials: Testimonial[] = [
  {
    name: "Sarah M.",
    role: "Senior Marketing Director",
    quote: "Meleena helped me find my voice in a male-dominated industry. After just 3 months of coaching, I negotiated a promotion and finally feel confident leading my team. Her blend of strategic thinking and mindfulness is truly transformative.",
    transformation: "Promoted to VP within 6 months",
    rating: 5
  },
  {
    name: "Priya K.",
    role: "MBA Graduate",
    quote: "I was overwhelmed with career options after graduation. Meleena's clarity sessions helped me discover my true strengths and land my dream role in consulting. Her approach combines practical career advice with deeper self-discovery.",
    transformation: "Landed dream job at top consulting firm",
    rating: 5
  },
  {
    name: "Jennifer L.",
    role: "Entrepreneur & Mother",
    quote: "Going through a major life transition, I felt lost. Meleena's women's empowerment coaching gave me the tools to rebuild my identity, set boundaries, and start my own business. I'm now living life on my own terms.",
    transformation: "Launched successful wellness business",
    rating: 5
  }
];

const defaultContent: WebsiteContent = {
  hero_badge_text: "Your AI-Powered Coaching Partner",
  hero_headline: "Partner with AI & Accelerate Your Practice",
  hero_subheadline: "GoCoachify combines cutting-edge AI tools with your coaching expertise to help you grow faster, serve more clients, and build the practice you've always envisioned.",
  hero_cta_primary: "Start Growing Today",
  hero_cta_secondary: "See How It Works",
  hero_stat_1_value: "10x",
  hero_stat_1_label: "Faster Growth",
  hero_stat_2_value: "500+",
  hero_stat_2_label: "Coaches Thriving",
  hero_stat_3_value: "AI",
  hero_stat_3_label: "Powered Tools",
  about_name: "Meleena Benance",
  about_title: "Founder, Tru Life Coaching",
  about_intro: "My journey weaves together engineering precision, business acumen, and a heart-centered passion for empowering people to live intentionally and lead authentically.",
  about_story: "Through all the strategy meetings and leadership milestones, one truth became clear — true success is not just about performance: it's about alignment and purpose.\n\nAs a certified yoga teacher, I integrate mindfulness, breathwork, and body awareness into my coaching approach. Because transformation isn't only mental — it's emotional, spiritual, and physical.",
  about_philosophy_title: "Coaching Philosophy",
  about_philosophy_text: "At Tru Life Coaching, I combine the discipline of an engineer, the strategic thinking of an MBA professional, the mindfulness and compassion of a yogi, and the empathy and insight of a coach.",
  about_quote: "True growth happens where logic meets intuition — where we don't just chase success, but cultivate balance, resilience, and purpose.",
  services_badge: "How I Can Help",
  services_title: "Three Pillars of Transformation",
  services_subtitle: "Every individual already holds the answers within them — my role is to help uncover truths, silence self-doubt, and rise with clarity.",
  services: defaultServices,
  packages_badge: "Investment in You",
  packages_title: "Coaching Packages",
  packages_subtitle: "Choose the journey that resonates with where you are and where you want to be.",
  packages: defaultPackages,
  packages_addons: defaultAddons,
  testimonials_badge: "Success Stories",
  testimonials_title: "Transformations That Inspire",
  testimonials_subtitle: "Real stories from clients who discovered their power, found their voice, and created meaningful change in their lives.",
  testimonials: defaultTestimonials,
  testimonials_cta_text: "Ready to write your own success story?",
  contact_badge: "Let's Connect",
  contact_title: "Ready to Transform Your Life?",
  contact_subtitle: "Take the first step towards the life you deserve. Book a discovery call to explore how we can work together.",
  contact_cta_title: "Book Your Discovery Session",
  contact_cta_description: "A complimentary 30-minute call to understand your goals and determine if we're the right fit for your journey.",
  contact_cta_button: "Schedule a Call",
  contact_quote: "Every individual already holds the answers within them — my role is to help them uncover the truths, silence the noise of self-doubt, and rise again with clarity.",
  contact_quote_author: "Meleena Benance",
  // Section visibility defaults
  show_about: true,
  show_services: true,
  show_packages: true,
  show_testimonials: true,
  show_videos: true,
  show_contact: true,
};

const uniqueProfileDefaults: Array<Partial<WebsiteContent>> = [
  {
    hero_badge_text: "Executive Clarity Studio",
    hero_headline: "Lead High-Stakes Teams Without Burning Out",
    hero_subheadline: "I help directors and founders build calm authority, strategic communication, and resilient teams during periods of rapid change.",
    hero_cta_primary: "Book Leadership Audit",
    hero_cta_secondary: "See Executive Programs",
    about_name: "Sarah Mitchell",
    about_title: "Executive Leadership Coach",
    services_title: "Leadership Systems That Scale",
    services_subtitle: "Practical leadership frameworks for team performance, influence, and sustainable growth.",
    services: [
      { icon: "Crown", title: "Executive Presence", subtitle: "For Directors & VPs", description: "Build influence in boardrooms, key meetings, and strategic initiatives.", highlights: ["High-stakes communication", "Executive storytelling", "Stakeholder alignment", "Confidence under pressure"], color: "primary" },
      { icon: "Users", title: "Team Performance", subtitle: "For People Leaders", description: "Create accountable team rhythms that improve ownership and execution.", highlights: ["Meeting cadence design", "Delegation systems", "Feedback architecture", "Conflict navigation"], color: "gold" },
      { icon: "Target", title: "Decision Mastery", subtitle: "For Founders & Operators", description: "Improve decision quality and speed with better thinking models.", highlights: ["Prioritization frameworks", "Risk mapping", "Scenario planning", "Focus discipline"], color: "accent" },
    ],
  },
  {
    hero_badge_text: "Career Momentum Lab",
    hero_headline: "Turn Career Uncertainty Into a Clear Next Move",
    hero_subheadline: "I coach ambitious professionals through role changes, promotions, and career reinvention with practical action plans.",
    hero_cta_primary: "Start Career Strategy",
    hero_cta_secondary: "View Career Tracks",
    about_name: "David Chen",
    about_title: "Career Transition Coach",
    services_title: "Career Growth, Structured",
    services_subtitle: "From self-assessment to job-market execution, every step is mapped and measurable.",
    services: [
      { icon: "Briefcase", title: "Career Transition", subtitle: "For Mid-Career Professionals", description: "Design and execute a confident transition into your next chapter.", highlights: ["Transferable skills mapping", "Target role strategy", "Opportunity pipeline", "Weekly accountability"], color: "primary" },
      { icon: "Lightbulb", title: "Personal Brand", subtitle: "For Job Seekers & Creators", description: "Position your value clearly across resume, LinkedIn, and interviews.", highlights: ["Brand messaging", "LinkedIn optimization", "Interview narratives", "Portfolio positioning"], color: "gold" },
      { icon: "Star", title: "Promotion Readiness", subtitle: "For High Performers", description: "Build the visibility, scope, and influence required for your next level.", highlights: ["Promotion case strategy", "Leadership communication", "Stakeholder buy-in", "Growth roadmap"], color: "accent" },
    ],
  },
  {
    hero_badge_text: "Wellness Reset Practice",
    hero_headline: "Rebuild Confidence, Boundaries, and Daily Energy",
    hero_subheadline: "I support women and caregivers navigating transitions, stress, and identity shifts with compassionate, evidence-informed coaching.",
    hero_cta_primary: "Begin Wellness Reset",
    hero_cta_secondary: "Explore Support Plans",
    about_name: "Maria Santos",
    about_title: "Wellness & Mindset Coach",
    services_title: "Whole-Person Coaching Support",
    services_subtitle: "A practical blend of mindset, emotional regulation, and everyday routines that restore balance.",
    services: [
      { icon: "Heart", title: "Emotional Resilience", subtitle: "For Stress Recovery", description: "Create healthier responses to stress and stop living in constant overdrive.", highlights: ["Nervous system regulation", "Self-talk reframing", "Burnout recovery habits", "Recovery planning"], color: "primary" },
      { icon: "GraduationCap", title: "Confidence Rebuild", subtitle: "For Major Life Changes", description: "Regain clarity and self-trust after transitions and setbacks.", highlights: ["Identity rebuilding", "Boundary scripts", "Values-based goals", "Decision confidence"], color: "gold" },
      { icon: "Users", title: "Relationship Boundaries", subtitle: "For Work & Home", description: "Improve communication and protect your energy in key relationships.", highlights: ["Boundary language", "Difficult conversation prep", "Role balance planning", "Support network mapping"], color: "accent" },
    ],
  },
];

const getUniqueDefaultContent = (coachId?: string): WebsiteContent => {
  const seed = coachId
    ? coachId.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0)
    : 0;
  const profile = uniqueProfileDefaults[seed % uniqueProfileDefaults.length];
  return { ...defaultContent, ...profile };
};

export const useWebsiteContent = (overrideCoachId?: string) => {
  const { coachId: contextCoachId } = useCoachContext();
  const coachId = overrideCoachId || contextCoachId;
  const [content, setContent] = useState<WebsiteContent>(getUniqueDefaultContent(coachId));
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      const defaults = getUniqueDefaultContent(coachId);
      let query = supabase
        .from("coach_website_content")
        .select("*");
      
      if (coachId) {
        query = query.eq("coach_id", coachId);
      }
      
      const { data, error } = await query.limit(1).maybeSingle();

      if (!error && data) {
        setContent({
          hero_badge_text: data.hero_badge_text || defaults.hero_badge_text,
          hero_headline: data.hero_headline || defaults.hero_headline,
          hero_subheadline: data.hero_subheadline || defaults.hero_subheadline,
          hero_cta_primary: data.hero_cta_primary || defaults.hero_cta_primary,
          hero_cta_secondary: data.hero_cta_secondary || defaults.hero_cta_secondary,
          hero_stat_1_value: data.hero_stat_1_value || defaults.hero_stat_1_value,
          hero_stat_1_label: data.hero_stat_1_label || defaults.hero_stat_1_label,
          hero_stat_2_value: data.hero_stat_2_value || defaults.hero_stat_2_value,
          hero_stat_2_label: data.hero_stat_2_label || defaults.hero_stat_2_label,
          hero_stat_3_value: data.hero_stat_3_value || defaults.hero_stat_3_value,
          hero_stat_3_label: data.hero_stat_3_label || defaults.hero_stat_3_label,
          about_name: data.about_name || defaults.about_name,
          about_title: data.about_title || defaults.about_title,
          about_intro: data.about_intro || defaults.about_intro,
          about_story: data.about_story || defaults.about_story,
          about_philosophy_title: data.about_philosophy_title || defaults.about_philosophy_title,
          about_philosophy_text: data.about_philosophy_text || defaults.about_philosophy_text,
          about_quote: data.about_quote || defaults.about_quote,
          services_badge: data.services_badge || defaults.services_badge,
          services_title: data.services_title || defaults.services_title,
          services_subtitle: data.services_subtitle || defaults.services_subtitle,
          services: (data.services as unknown as Service[]) || defaults.services,
          packages_badge: data.packages_badge || defaults.packages_badge,
          packages_title: data.packages_title || defaults.packages_title,
          packages_subtitle: data.packages_subtitle || defaults.packages_subtitle,
          packages: (data.packages as unknown as PackageCategory[]) || defaults.packages,
          packages_addons: (data.packages_addons as unknown as AddOn[]) || defaults.packages_addons,
          testimonials_badge: data.testimonials_badge || defaults.testimonials_badge,
          testimonials_title: data.testimonials_title || defaults.testimonials_title,
          testimonials_subtitle: data.testimonials_subtitle || defaults.testimonials_subtitle,
          testimonials: (data.testimonials as unknown as Testimonial[]) || defaults.testimonials,
          testimonials_cta_text: data.testimonials_cta_text || defaults.testimonials_cta_text,
          contact_badge: data.contact_badge || defaults.contact_badge,
          contact_title: data.contact_title || defaults.contact_title,
          contact_subtitle: data.contact_subtitle || defaults.contact_subtitle,
          contact_cta_title: data.contact_cta_title || defaults.contact_cta_title,
          contact_cta_description: data.contact_cta_description || defaults.contact_cta_description,
          contact_cta_button: data.contact_cta_button || defaults.contact_cta_button,
          contact_quote: data.contact_quote || defaults.contact_quote,
          contact_quote_author: data.contact_quote_author || defaults.contact_quote_author,
          // Section visibility
          show_about: data.show_about ?? true,
          show_services: data.show_services ?? true,
          show_packages: data.show_packages ?? true,
          show_testimonials: data.show_testimonials ?? true,
          show_videos: data.show_videos ?? true,
          show_contact: data.show_contact ?? true,
        });
      }
      if (!data) {
        setContent(defaults);
      }
      setIsLoading(false);
    };

    fetchContent();
  }, [coachId]);

  return { content, isLoading, defaultContent };
};
